import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey, WorkflowExecutionFull } from '../model';
import { isNodeTerminal, watchNodeTerminal } from './executionStreamManager';

type Options = {
  onUpdate: () => void;
};

export function useNodeLogsStream(
  executionId: string | null | undefined,
  nodeId: string | null | undefined,
  enabled: boolean,
  { onUpdate }: Options,
) {
  const queryClient = useQueryClient();
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  });

  useEffect(() => {
    if (!enabled || !executionId || !nodeId) return;
    if (isNodeTerminal(queryClient, executionId, nodeId)) return;

    const readRevision = (): number | undefined => {
      const execution = queryClient.getQueryData<WorkflowExecutionFull>([
        QueryKey.WorkflowExecutions,
        { id: executionId },
      ]);
      return execution?.node_progress?.find((n) => n.node_id === nodeId)?.log_revision;
    };

    let lastRevision = readRevision() ?? -1;

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const current = readRevision();
      if (current !== undefined && current > lastRevision) {
        lastRevision = current;
        onUpdateRef.current();
      }
    });

    const releaseWatch = watchNodeTerminal(queryClient, executionId, nodeId, unsubscribe);

    return () => {
      releaseWatch();
      unsubscribe();
    };
  }, [executionId, nodeId, enabled, queryClient]);
}
